/* Stack Finder — Tasting-Note Glossary (web)
   Makes the tasting-note pills on the website Stack Finder cards (.cs-note-pill)
   tappable: tap a note → small popover explaining what it means.
   Definitions cover the full 91-term v2.1 vocabulary (vocab-families.json),
   so any note a card shows is explained. Decoupled — observes the card DOM,
   no change to the recommendation engine.
   © 2026 Cigar Stacks LLC. Stack Finder. Patent Pending. */
(function () {
  "use strict";

  // note (lowercase) -> { d: short plain-English meaning, f: flavor family }
  var DEFS = {
    // Woody / Earthy
    "earth": ["Damp soil and forest-floor character — earthy and savory.", "Woody / Earthy"],
    "cedar": ["Aromatic cedar wood, like the inside of a Spanish cigar box.", "Woody / Earthy"],
    "wood": ["A clean, dry woody note, like seasoned timber.", "Woody / Earthy"],
    "oak": ["Toasted oak, like a whiskey or wine barrel.", "Woody / Earthy"],
    "leather": ["Supple saddle-leather note — a classic sign of well-aged tobacco.", "Woody / Earthy"],
    "hay": ["Fresh-cut dried grass; light and rustic.", "Woody / Earthy"],
    "dry wood": ["Crisp, kiln-dried wood — sharper than plain 'wood.'", "Woody / Earthy"],
    "hickory": ["Smoky hardwood, like barbecue smoke.", "Woody / Earthy"],
    "smoke": ["Charred, campfire smokiness on the palate.", "Woody / Earthy"],
    "campfire": ["Woodsmoke and char, like sitting by a fire.", "Woody / Earthy"],
    "mushroom": ["Earthy, savory fungus note often found in aged cigars.", "Woody / Earthy"],
    // Sweet
    "sweetness": ["A general natural sweetness on the lips and palate.", "Sweet"],
    "caramel": ["Cooked-sugar caramel — rich and buttery-sweet.", "Sweet"],
    "vanilla": ["Soft, creamy vanilla-bean sweetness.", "Sweet"],
    "honey": ["Floral, golden honey sweetness.", "Sweet"],
    "molasses": ["Dark, heavy brown-sugar sweetness.", "Sweet"],
    "butterscotch": ["Buttery brown-sugar candy note.", "Sweet"],
    "cocoa": ["Unsweetened cocoa powder — chocolatey but dry.", "Sweet"],
    "dark chocolate": ["Rich, slightly bitter dark-chocolate note.", "Sweet"],
    "milk chocolate": ["Smooth, sweeter, creamier chocolate.", "Sweet"],
    "white chocolate": ["Sweet, creamy cocoa-butter note without the bitterness.", "Sweet"],
    "mocha": ["A blend of coffee and chocolate.", "Sweet"],
    "maple": ["Warm, woody maple-syrup sweetness.", "Sweet"],
    "nougat": ["Soft, sweet, nutty confection note.", "Sweet"],
    "toffee": ["Buttery burnt-sugar candy note.", "Sweet"],
    "marzipan": ["Sweet almond-paste note.", "Sweet"],
    "dulce de leche": ["Caramelized-milk sweetness — rich and creamy.", "Sweet"],
    "marshmallow": ["Light, airy, sugary-sweet note.", "Sweet"],
    // Spicy
    "pepper": ["General peppery spice and a tingle on the tongue.", "Spicy"],
    "black pepper": ["Sharp, familiar black-pepper bite.", "Spicy"],
    "white pepper": ["Milder, rounder pepper spice.", "Spicy"],
    "red pepper": ["Hot, capsicum-style red-pepper heat.", "Spicy"],
    "spice": ["A general warm baking-spice character.", "Spicy"],
    "cinnamon": ["Warm, sweet cinnamon spice.", "Spicy"],
    "anise": ["Black-licorice / fennel spice.", "Spicy"],
    "nutmeg": ["Warm, woody baking spice.", "Spicy"],
    "baking spices": ["A cinnamon-nutmeg-clove medley, like spiced baking.", "Spicy"],
    "ginger": ["Zesty, warm ginger spice.", "Spicy"],
    "clove": ["Pungent, sweet-hot clove spice.", "Spicy"],
    "licorice": ["Sweet black-licorice / anise note.", "Spicy"],
    "gingerbread": ["Spiced-molasses cookie note.", "Spicy"],
    // Nutty
    "nuts": ["A general roasted-nut character.", "Nutty"],
    "almond": ["Mild, sweet roasted-almond note.", "Nutty"],
    "hazelnut": ["Rich, sweet hazelnut (think Nutella).", "Nutty"],
    "walnut": ["Earthy, slightly bitter walnut.", "Nutty"],
    "cashew": ["Soft, buttery, sweet cashew.", "Nutty"],
    "peanut": ["Roasted-peanut / peanut-shell note.", "Nutty"],
    // Fruity
    "citrus": ["Bright, zesty citrus note.", "Fruity"],
    "orange peel": ["Fragrant orange-zest note.", "Fruity"],
    "lemon peel": ["Sharp lemon-zest brightness.", "Fruity"],
    "stone fruit": ["Juicy peach / apricot character.", "Fruity"],
    "cherry": ["Sweet-tart cherry note.", "Fruity"],
    "black cherry": ["Deep, dark, jammy cherry.", "Fruity"],
    "berry": ["A mixed sweet-berry note.", "Fruity"],
    "dried fruit": ["Concentrated raisin / fig dried-fruit sweetness.", "Fruity"],
    "plum": ["Dark, sweet, jammy plum.", "Fruity"],
    "fig": ["Sweet, jammy dried-fig note.", "Fruity"],
    "dates": ["Rich, honey-sweet date note.", "Fruity"],
    "raisin": ["Sweet, concentrated dried-grape note.", "Fruity"],
    // Roasted
    "coffee": ["Roasted coffee-bean note.", "Roasted"],
    "espresso": ["Intense, dark roasted-espresso note.", "Roasted"],
    "toast": ["Warm toasted-bread note.", "Roasted"],
    "cappuccino": ["Coffee softened with creamy, milky notes.", "Roasted"],
    // Creamy
    "cream": ["Smooth, soft, dairy-like creaminess.", "Creamy"],
    "buttery": ["Rich, melted-butter smoothness.", "Creamy"],
    "buttery smoke": ["Smooth smoke with a buttery roundness.", "Creamy"],
    "buttercream": ["Sweet, rich butter-and-sugar frosting note.", "Creamy"],
    // Floral / Herbal
    "floral": ["Light flower-blossom aromatics.", "Floral / Herbal"],
    "herbal": ["A green, herb-garden note.", "Floral / Herbal"],
    "mint": ["Cool, fresh mint note.", "Floral / Herbal"],
    "tea leaves": ["Dry, tannic black-tea note.", "Floral / Herbal"],
    // Umami / Savory
    "meaty": ["Savory, cured-meat / umami note.", "Umami / Savory"],
    "savory": ["Salty-umami, non-sweet richness.", "Umami / Savory"],
    "bread": ["Fresh, doughy baked-bread note.", "Umami / Savory"],
    "grain": ["A cereal / grain note, like raw wheat.", "Umami / Savory"],
    // Mineral / Other
    "salt": ["A subtle salty, mineral note.", "Mineral / Other"],
    "sea salt": ["A clean, briny sea-salt note.", "Mineral / Other"],
    // Pairing-Adjacent
    "whiskey": ["Reminiscent of whiskey — oak, caramel and warmth.", "Pairing note"],
    "bourbon": ["Sweet, oaky, vanilla bourbon character.", "Pairing note"],
    "scotch": ["Smoky, malty scotch character.", "Pairing note"],
    "cognac": ["Fruity, oaky brandy note.", "Pairing note"],
    "rum": ["Sweet, molasses-like rum note.", "Pairing note"],
    "stout": ["Roasty, chocolatey dark-beer note.", "Pairing note"],
    "ipa": ["Hoppy, bitter, citrusy beer note.", "Pairing note"],
    "malt": ["Sweet, cereal, malted-grain note.", "Pairing note"],
    "chardonnay": ["Buttery, oaky white-wine note.", "Pairing note"],
    // Structural
    "tannic": ["A dry, grippy mouthfeel, like strong tea or red wine.", "Mouthfeel"],
    "powdery": ["A fine, dry, chalky texture.", "Mouthfeel"],
    "tangy": ["Bright, mouthwatering tartness.", "Mouthfeel"],
    "bitter": ["Sharp, dry bitterness, like espresso or cocoa.", "Mouthfeel"],
    "acidic": ["Zippy, sour brightness.", "Mouthfeel"]
  };

  function lookup(note) {
    var k = String(note == null ? "" : note).trim().toLowerCase();
    if (DEFS[k]) return DEFS[k];
    // tolerant fallbacks (plurals / punctuation)
    if (DEFS[k.replace(/s$/, "")]) return DEFS[k.replace(/s$/, "")];
    return [null, null];
  }

  var GREEN = "#7EDB9A";
  function injectStyles() {
    if (document.getElementById("cs-tn-styles")) return;
    var s = document.createElement("style");
    s.id = "cs-tn-styles";
    s.textContent =
      ".cs-note-pill{cursor:pointer;}" +
      ".cs-note-pill:hover{filter:brightness(1.12);}" +
      ".cs-note-pills[data-tn-hint]::before{content:'👇 Tap a note to learn what it means';" +
        "grid-column:1 / -1;flex-basis:100%;width:100%;display:block;font-size:10px;letter-spacing:.08em;text-transform:uppercase;" +
        "font-weight:700;color:rgba(126,219,154,.85);margin:2px 0 6px;opacity:.9;}" +
      ".cs-tn-pop{position:absolute;z-index:99999;max-width:260px;background:#0f1815;color:#f3f7f4;" +
        "border:1.5px solid " + GREEN + ";border-radius:12px;padding:12px 14px 13px;" +
        "box-shadow:0 12px 34px rgba(0,0,0,.5);font-family:'Montserrat',sans-serif;line-height:1.45;" +
        "opacity:0;transform:translateY(4px);transition:opacity .12s,transform .12s;pointer-events:none;}" +
      ".cs-tn-pop.cs-tn-show{opacity:1;transform:translateY(0);pointer-events:auto;}" +
      ".cs-tn-pop-term{font-weight:900;font-size:14px;letter-spacing:.02em;color:" + GREEN + ";margin:0 0 2px;}" +
      ".cs-tn-pop-fam{font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:rgba(255,255,255,.55);" +
        "font-family:'Courier New',monospace;margin:0 0 7px;}" +
      ".cs-tn-pop-def{font-size:13px;color:rgba(243,247,244,.92);}" +
      ".cs-tn-pop-x{position:absolute;top:6px;right:9px;color:rgba(255,255,255,.5);font-size:15px;cursor:pointer;line-height:1;}";
    document.head.appendChild(s);
  }

  var pop = null;
  function ensurePop() {
    if (pop) return pop;
    pop = document.createElement("div");
    pop.className = "cs-tn-pop";
    pop.innerHTML = '<span class="cs-tn-pop-x">×</span>' +
      '<div class="cs-tn-pop-term"></div><div class="cs-tn-pop-fam"></div><div class="cs-tn-pop-def"></div>';
    document.body.appendChild(pop);
    pop.querySelector(".cs-tn-pop-x").addEventListener("click", hidePop);
    return pop;
  }
  function hidePop() { if (pop) pop.classList.remove("cs-tn-show"); }

  function showPop(pill) {
    var note = pill.textContent || "";
    var info = lookup(note);
    var p = ensurePop();
    p.querySelector(".cs-tn-pop-term").textContent = note.trim();
    var famEl = p.querySelector(".cs-tn-pop-fam");
    famEl.textContent = info[1] || "";
    famEl.style.display = info[1] ? "" : "none";
    p.querySelector(".cs-tn-pop-def").textContent =
      info[0] || "A tasting note our blenders detected in this cigar.";
    // position below the pill, clamped to the viewport
    p.classList.add("cs-tn-show");
    var r = pill.getBoundingClientRect();
    var pw = p.offsetWidth, ph = p.offsetHeight;
    var left = r.left + window.scrollX;
    left = Math.min(left, window.scrollX + document.documentElement.clientWidth - pw - 10);
    left = Math.max(left, window.scrollX + 8);
    var top = r.bottom + window.scrollY + 8;
    // flip above if not enough room below
    if (r.bottom + ph + 16 > document.documentElement.clientHeight) {
      top = r.top + window.scrollY - ph - 8;
    }
    p.style.left = left + "px";
    p.style.top = top + "px";
  }

  function addHints() {
    document.querySelectorAll(".cs-note-pills:not([data-tn-hint])").forEach(function (g) {
      g.setAttribute("data-tn-hint", "1");
    });
  }

  function init() {
    injectStyles();
    addHints();
    // pills render/re-render as results change — keep hints current
    var results = document.querySelector('[id^="cs-results-"]');
    if (results) new MutationObserver(addHints).observe(results, { childList: true, subtree: true });
    // one delegated handler covers all current + future pills
    document.addEventListener("click", function (e) {
      var pill = e.target && e.target.closest ? e.target.closest(".cs-note-pill") : null;
      if (pill) { e.stopPropagation(); showPop(pill); return; }
      if (pop && !pop.contains(e.target)) hidePop();
    }, true);
    document.addEventListener("keydown", function (e) { if (e.key === "Escape") hidePop(); });
    window.addEventListener("scroll", hidePop, true);
    window.addEventListener("resize", hidePop);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
